import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import { useTranslation } from 'react-i18next';
import {
  fetchPushPreferences,
  updatePushPreferences,
} from '../../api/account';
import { fetchPushStatus } from '../../api/push';
import {
  disablePushNotifications,
  enablePushNotifications,
  getCurrentPushSubscription,
  getNotificationPermission,
  isPushSupported,
} from '../../utils/pushNotifications';

const defaultPreferences = {
  companionRequests: true,
  companionAcceptance: true,
  nightInvitations: true,
  nightUpdates: true,
  nightReminders: true,
  communityAnnouncements: false,
};

const preferenceKeys = [
  'companionRequests',
  'companionAcceptance',
  'nightInvitations',
  'nightUpdates',
  'nightReminders',
  'communityAnnouncements',
];

const normalizePreferences = (preferences = {}) => ({
  ...defaultPreferences,
  companionRequests: preferences.companionRequests !== false,
  companionAcceptance: preferences.companionAcceptance !== false,
  nightInvitations: preferences.nightInvitations !== false,
  nightUpdates: preferences.nightUpdates !== false,
  nightReminders: preferences.nightReminders !== false,
  communityAnnouncements: preferences.communityAnnouncements === true,
});

const PushPreferencesPanel = () => {
  const { t } = useTranslation();
  const [preferences, setPreferences] = useState(defaultPreferences);
  const [permission, setPermission] = useState(getNotificationPermission());
  const [activeSubscriptions, setActiveSubscriptions] = useState(0);
  const [serverConfigured, setServerConfigured] = useState(false);
  const [localSubscribed, setLocalSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorKey, setErrorKey] = useState('');

  const refreshDeviceStatus = async () => {
    if (!isPushSupported()) {
      setPermission('unsupported');
      setLocalSubscribed(false);
      return;
    }

    setPermission(getNotificationPermission());
    const subscription = await getCurrentPushSubscription();
    setLocalSubscribed(Boolean(subscription));
  };

  useEffect(() => {
    let mounted = true;

    Promise.all([fetchPushPreferences(), fetchPushStatus()])
      .then(async ([preferenceData, statusData]) => {
        if (!mounted) return;

        setPreferences(normalizePreferences(preferenceData.preferences));
        setServerConfigured(Boolean(statusData.configured));
        setActiveSubscriptions(Number(statusData.activeSubscriptions || 0));
        await refreshDeviceStatus();
        setErrorKey('');
      })
      .catch(() => {
        if (mounted) setErrorKey('notifications.push.error');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const handleToggle = (key) => (event) => {
    setSuccess(false);
    setErrorKey('');
    setPreferences((current) => ({
      ...current,
      [key]: event.target.checked,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSuccess(false);
    setErrorKey('');

    try {
      const data = await updatePushPreferences(preferences);
      setPreferences(normalizePreferences(data.preferences));
      setSuccess(true);
    } catch {
      setErrorKey('notifications.push.error');
    } finally {
      setSaving(false);
    }
  };

  const handleEnable = async () => {
    setBusy(true);
    setSuccess(false);
    setErrorKey('');

    try {
      const result = await enablePushNotifications();
      if (result.status === 'granted') {
        const status = await fetchPushStatus();
        setActiveSubscriptions(Number(status.activeSubscriptions || 0));
        setServerConfigured(Boolean(status.configured));
        await refreshDeviceStatus();
        setSuccess(true);
      } else if (result.status === 'server-unconfigured') {
        setErrorKey('notifications.push.serverUnconfigured');
      } else if (result.status === 'denied') {
        setErrorKey('notifications.push.denied');
      } else {
        setErrorKey('notifications.push.unsupported');
      }
    } catch {
      setErrorKey('notifications.push.error');
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    setSuccess(false);
    setErrorKey('');

    try {
      await disablePushNotifications();
      const status = await fetchPushStatus();
      setActiveSubscriptions(Number(status.activeSubscriptions || 0));
      await refreshDeviceStatus();
      setSuccess(true);
    } catch {
      setErrorKey('notifications.push.error');
    } finally {
      setBusy(false);
    }
  };

  const currentDeviceEnabled = permission === 'granted' && localSubscribed;
  const canEnable = isPushSupported() && serverConfigured && permission !== 'denied';

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 0.5 }}>
          {t('notifications.eyebrow')}
        </Typography>
        <Typography variant="h3">{t('notifications.push.title')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          {t('notifications.push.subtitle')}
        </Typography>
      </Box>

      {loading ? (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CircularProgress size={20} />
          <Typography color="text.secondary">
            {t('notifications.push.loading')}
          </Typography>
        </Stack>
      ) : (
        <>
          <Alert
            severity={currentDeviceEnabled ? 'success' : 'info'}
            icon={
              currentDeviceEnabled ? (
                <NotificationsActiveOutlinedIcon />
              ) : (
                <NotificationsOffOutlinedIcon />
              )
            }
          >
            {currentDeviceEnabled
              ? t('notifications.push.deviceEnabled', {
                  count: activeSubscriptions,
                })
              : t('notifications.push.deviceDisabled')}
          </Alert>

          {!serverConfigured ? (
            <Alert severity="warning">
              {t('notifications.push.serverUnconfigured')}
            </Alert>
          ) : null}

          {permission === 'denied' ? (
            <Alert severity="warning">{t('notifications.push.denied')}</Alert>
          ) : null}

          {!isPushSupported() ? (
            <Alert severity="info">{t('notifications.push.unsupported')}</Alert>
          ) : null}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="contained"
              startIcon={
                busy && !currentDeviceEnabled ? (
                  <CircularProgress color="inherit" size={18} />
                ) : (
                  <NotificationsActiveOutlinedIcon />
                )
              }
              onClick={handleEnable}
              disabled={busy || currentDeviceEnabled || !canEnable}
            >
              {t('notifications.push.enable')}
            </Button>
            <Button
              variant="outlined"
              startIcon={
                busy && currentDeviceEnabled ? (
                  <CircularProgress color="inherit" size={18} />
                ) : (
                  <NotificationsOffOutlinedIcon />
                )
              }
              onClick={handleDisable}
              disabled={busy || !localSubscribed}
            >
              {t('notifications.push.disable')}
            </Button>
          </Stack>

          <Stack spacing={1}>
            {preferenceKeys.map((key) => (
              <FormControlLabel
                key={key}
                control={
                  <Switch
                    checked={Boolean(preferences[key])}
                    onChange={handleToggle(key)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle2">
                      {t(`notifications.push.options.${key}.label`)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t(`notifications.push.options.${key}.description`)}
                    </Typography>
                  </Box>
                }
                sx={{
                  alignItems: 'flex-start',
                  ml: 0,
                  gap: 1,
                  '& .MuiFormControlLabel-label': { flex: 1 },
                }}
              />
            ))}
          </Stack>
        </>
      )}

      {success ? (
        <Alert severity="success">{t('notifications.push.success')}</Alert>
      ) : null}
      {errorKey ? <Alert severity="error">{t(errorKey)}</Alert> : null}

      <Button
        variant="contained"
        onClick={handleSave}
        disabled={loading || saving}
        sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
      >
        {saving ? t('notifications.push.saving') : t('notifications.push.save')}
      </Button>
    </Stack>
  );
};

export default PushPreferencesPanel;
